import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "../../../../../util/api";
import { QueryKeys } from "../../../../../util/types";

const LIMIT = 20;

export const useGetPromotionCodes = () => {
  const [cursors, setCursors] = useState<string[]>([]); // stack of starting_after cursors for prev pages
  const [currentCursor, setCurrentCursor] = useState<string | undefined>(
    undefined
  );
  const [search, setSearch] = useState("");
  const [searchDebounced, setSearchDebounced] = useState("");

  // Debounce search and reset pagination in the same update, so the old
  // cursor is never requested together with the new search term
  useEffect(() => {
    if (search === searchDebounced) return;
    const timer = setTimeout(() => {
      setSearchDebounced(search);
      setCurrentCursor(undefined);
      setCursors([]);
    }, 400);
    return () => clearTimeout(timer);
  }, [search, searchDebounced]);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: [
      QueryKeys.LIST_PROMOTION_CODES,
      currentCursor ?? "",
      searchDebounced,
    ],
    queryFn: async () =>
      (await api.getPromotionCodes(
        LIMIT,
        currentCursor,
        searchDebounced || undefined
      )) ?? { data: [], hasMore: false },
  });

  const promoCodes = data?.data ?? [];
  const hasMore = data?.hasMore ?? false;

  const goToNextPage = () => {
    if (promoCodes.length > 0) {
      const lastId = promoCodes[promoCodes.length - 1].id;
      setCursors((prev) => [...prev, currentCursor ?? ""]);
      setCurrentCursor(lastId);
    }
  };

  const goToPrevPage = () => {
    const prev = [...cursors];
    const prevCursor = prev.pop();
    setCursors(prev);
    setCurrentCursor(prevCursor || undefined);
  };

  const page = cursors.length + 1;
  const hasPrev = cursors.length > 0;

  return {
    promoCodes,
    isLoading,
    error: error ? String(error) : null,
    hasMore,
    hasPrev,
    page,
    goToNextPage,
    goToPrevPage,
    search,
    setSearch,
    reload: refetch,
  };
};
